import Styles from './UserNodes.module.scss';
import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
// Module imports.
import CryptoJS from 'crypto-js';
import { Box, Tooltip, Typography, Dialog } from '@mui/material';
import PlayCircleFilledIcon from '@mui/icons-material/PlayCircleFilled';
import LockIcon from '@mui/icons-material/Lock';
// Components import.
import { AivagamButton } from 'components/global/AivagamButton/AivagamButton';
import { useAPI, Message, AuthContext } from 'components/lib';
import { LoadingBackdrop } from 'components/global/LoadingBackdrop/LoadingBackdrop';
import NodeViewCard from 'components/global/NodeViewCard/NodeViewCard';
import { LabInfo } from 'components/global/NodeViewCard/LabInfo';
import { BreadCrumbs } from 'components/global/BreadCrumbs/BreadCrumbs';
import { useContext } from 'react';
import { ReportDialogue } from './ReportDialogue';

export function UserNodes(props) {

	const decrypt = (cipherText) => {
		const bytes = CryptoJS.AES.decrypt(cipherText, 'secretKey');
		return bytes.toString(CryptoJS.enc.Utf8);
	};
	const { lab_id } = useParams();
	const navigate = useNavigate();
	let lastNodeStatus = '';

	const storedCertID = sessionStorage.getItem("certID");
	const cert_id = storedCertID && decrypt(storedCertID);

	const [nodeList, setNodeList] = useState([]);
	const [infoData, setInfoData] = useState();
	const { data, loading } = useAPI(`/api/user/labs/${lab_id}/nodes?certId=${cert_id}`, 'GET');
	const isFreeLab = lab_id === process.env.REACT_APP_FREE_LAB_ID;
	const context = useContext(AuthContext)
	const [showNotOnboardedDialog, setShowNotOnboardedDialog] = useState(false);
	const [reportDialogue, setReportDialogue] = useState(false);
	const handleClick = (data) => {
		const id = data;
		navigate(`/labs/${lab_id}/nodes/${id}`);
	};

	useEffect(() => {
		if (data) {
			setInfoData(data?.labDetails);
			document.title = props.t('Labs.UserNodes.title.title', { LabName: data.labDetails.labName })
			const updatedNodeList = data?.nodes?.map((node) => ({
				...node,
				isDisabled: node.status === 'not_started',
				isCompleted: node.status === 'completed',
				buttonText:
					node.status === 'not_started' ? props.t('Labs.UserNodes.nodes.button.locked') : node.status === 'completed' ? props.t('Labs.UserNodes.nodes.button.completed') : props.t('Labs.UserNodes.nodes.button.start'),
			}));
			setNodeList(updatedNodeList);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [data]);

	lastNodeStatus = nodeList[nodeList.length - 1]?.status;

	const handleCertificateClick = async () => {
		navigate(`/certificate/${infoData?.user_cert_id}`)
	};

	const quizButtonText =
		lastNodeStatus !== 'completed' ? props.t('Labs.UserNodes.quiz.button.quiz') : !infoData.quizAttemptStatus ? props.t('Labs.UserNodes.quiz.button.complete') : props.t('Labs.UserNodes.quiz.button.start');

	const handleUserManualClick = () => {
		navigate(`/labs/${lab_id}/local`);

	};

	const handleReport = () => {
		setReportDialogue(true);
	}

	const handleGetReportClick = () => {
		navigate(`/labs/${lab_id}/report`)
	};

	const status = infoData?.subscriptionStatus;

	const buttonText = infoData?.certID ? 'Buy Full Certificate' : 'Buy Full Course';
	const buttonLink = infoData?.certID
		? `/lpaths/info/${infoData?.certID}`
		: `/labs/info/${lab_id}`;

	const messageConfig = {
		certificate: {
			title: props.t('Labs.UserNodes.message.title'),
			text: props.t('Labs.UserNodes.message.text'),
			type: 'success',
			closable: true
		},
		trial: {
			title: 'You are on Free Trial',
			text: 'You only have access to the first module. Upgrade now to unlock all remaining modules and complete your certification!',
			type: 'warning',
			buttonText,
			buttonLink
		},
		trial_expired: {
			title: 'Your Free Trial Has Ended',
			text: 'Your free trial has ended. Access to all modules is now locked. Upgrade your plan to continue learning and unlock the full course.',
			type: 'error',
			buttonText,
			buttonLink
		}
	};

	// pick which message to show
	let selectedMessage = null;

	if (infoData?.certificate && !isFreeLab) {
		selectedMessage = messageConfig.certificate;
	} else if (!isFreeLab && messageConfig[status]) {
		selectedMessage = messageConfig[status];
	}

	return (
		<> 
			{selectedMessage && (
				<Message
					{...selectedMessage}
				/>
			)}
			<>
				<div className={Styles.CardContainer}>
					<>
						<div className={Styles.tabContainer}>
							<BreadCrumbs
								onClickPaths={[
									infoData?.certID ? '/lpaths' : '',
									infoData?.certID ? `/lpaths/${infoData?.certID}/learningpath` : infoData?.isProject === 1 ? '/projects' : '/labs',
									'',
								]}
								crumbs={[
									infoData?.certID ? props.t('Labs.UserNodes.breadcrumbs.lpath') : '',
									infoData?.certName ? infoData?.certName : infoData?.isProject === 1 ? props.t('CapstoneProject.ProjectNodes.breadcrumbs.labs') : props.t('Labs.UserNodes.breadcrumbs.labs'),
									infoData?.labName ? `${infoData?.labName}: ${props.t('Labs.UserNodes.breadcrumbs.nodes')}` : props.t('Labs.UserNodes.breadcrumbs.nodes'),
								]}
							/>
							<Tooltip
								title={
									(infoData?.certificate) ? (
										<Typography fontSize={16}>{props.t('Labs.UserNodes.button.tooltip.enabled')}</Typography>
									) : (
										<Typography fontSize={16}>{props.t('Labs.UserNodes.button.tooltip.disabled')}</Typography>
									)
								}
								arrow
								placement="top"
							>
								<span style={{ display: isFreeLab ? 'none' : '' }}>
									<button
										id="certButton"
										className={Styles.certButton}
										onClick={() => {
											if (!context?.user?.onboarded) {
												setShowNotOnboardedDialog(true);
												return;
											}
											handleCertificateClick();

										}}
										disabled={!infoData?.certificate}
									>
										{props.t('Labs.UserNodes.button.text')}
									</button>
									<Dialog
										open={showNotOnboardedDialog}
										onClose={() => setShowNotOnboardedDialog(false)}
										maxWidth="xs"
									>
										<div className={Styles.tcContainer} style={{ minWidth: 360, padding: 20 }}>
											<h3 className={Styles.tcTitle}>{props.t('Labs.UserNodes.profile.heading')}</h3>
											<p className={Styles.tcBody}>
												{props.t('Labs.UserNodes.profile.message')}
											</p>

											<div className={Styles.tcActions}>
												<AivagamButton
													id="closeNotOnboarded"
													buttonText={props.t('Labs.UserNodes.button.close')}
													onClick={() => setShowNotOnboardedDialog(false)}
													type="cancel"
												/>
												<AivagamButton
													id="goToAccount"
													buttonText={props.t('Labs.UserNodes.button.account')}
													onClick={() => {
														setShowNotOnboardedDialog(false);
														navigate("/account");
													}}
													type="confirm"
												/>
											</div>
										</div>
									</Dialog>
								</span>
							</Tooltip>
						</div>
						<div id='userManualButtonContainer' className={Styles.userManualButtonContainer}>
							{!!infoData?.isProject && (
								<div className={Styles.userManualButton}>
									<AivagamButton
										buttonText={props.t('Labs.UserNodes.button.create')}
										disabled={!infoData?.certificate || !infoData?.onboarded}
										onClick={handleReport}
										type="action"
									>
									</AivagamButton>
								</div>
							)}
							{infoData?.userManual ? (
								<div className={Styles.userManualButton}
								>
									<AivagamButton
										buttonText={props.t('Labs.UserNodes.button.local')}

										onClick={handleUserManualClick}
									>
									</AivagamButton>
								</div>
							) : (
								<div>
								</div>
							)}
						</div>
						<div style={{ display: 'flex', flexWrap: 'wrap' }}>
							{loading ? (
								<div>
									<LoadingBackdrop isOpen={true} />
								</div>
							) : nodeList.length > 0 ? (
								nodeList.map((node) => (
									<NodeViewCard
										data={node}
										key={node.id}
										onClick={handleClick}
										button_text={node.buttonText}
										trialExpired={infoData?.subscriptionStatus === 'trial_expired'}
									/>
								))
							) : (
								'No Value'
							)}
						</div>
					</>
					{loading ? (
						''
					) : (
						<>
							<Box
								sx={{
									display: 'flex',
									width: '100%',
									border: (theme) => `1px solid ${theme.palette.info}`,
									borderRadius: 1,
									bgcolor: 'transparent',
									color: 'text.secondary',
									'& svg': { m: 1.5 },
									'& hr': { mx: 0.5 },
									'& > :not(style)': { m: 1 },
								}}
							>
								<LabInfo
									attempts={infoData?.quizAttempts}
									quizScore={infoData?.quizScore}
									totalScore={infoData?.totalScore}
								/>
							</Box>

							<div id="quizButtonContainer" className={Styles.quizButtonContainer}>
								<button
									variant="contained"
									color="primary"
									disabled={lastNodeStatus !== 'completed' || !infoData.quizAttemptStatus}
									onClick={() => {
										navigate(`/labs/${lab_id}/quiz/start`);
									}}
								>
									{(lastNodeStatus !== 'completed' || !infoData.quizAttemptStatus) && <LockIcon sx={{ mr: '4px' }} />}
									{lastNodeStatus === 'completed' && infoData.quizAttemptStatus && (
										<PlayCircleFilledIcon fontSize="small" sx={{ mr: '4px' }} />
									)}
									{quizButtonText}
								</button>
							</div>
						</>
					)}
				</div>
				<ReportDialogue
					open={reportDialogue}
					onSave={handleGetReportClick}
					onClose={() => setReportDialogue(false)}
				/>
			</>
		</>
	);
}